import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { EventTypeEnum, useEventExploration } from '@verantos-dev/app-state-cohort-builder';
import type { AnalyticalVariable } from '@verantos-dev/types-cohorts';

import { useAVLogicErrors } from './useAVLogicErrors';
import type { LogicNode } from '../../utils/analyticalVariable.utils';
import {
  extractConceptNames,
  getNodeDescription,
  isDomainFilter,
} from '../../utils/analyticalVariable.utils';
import { useAnalyticalVariableCardContext } from '../AnalyticalVariableCard/AnalyticalVariableCardContext';

export const useAVLogicCard = (analyticalVariable: AnalyticalVariable) => {
  const intl = useIntl();
  const { onViewEventClick } = useEventExploration(EventTypeEnum.AV);
  const { onDrillDownClick: contextDrillDownClick } = useAnalyticalVariableCardContext();

  // Honors the nullable contract on AnalyticalVariable.logic (OR-6069):
  // SQL-only AVs carry logic: null; consumers below handle the absent case.
  const logicNode: LogicNode | null = analyticalVariable.logic;

  const boldTermFromDescription = useMemo(() => {
    const match = analyticalVariable.description?.match(/\*\*(.*?)\*\*/);
    return match?.[1];
  }, [analyticalVariable.description]);

  const logicHeaderName = useMemo(() => {
    if (!logicNode) return boldTermFromDescription ?? analyticalVariable.name;
    const desc = getNodeDescription(logicNode);
    const descCapitalized = desc ? desc[0].toUpperCase() + desc.slice(1) : undefined;
    if (descCapitalized) return descCapitalized;
    if (isDomainFilter(logicNode)) {
      const conceptRef = (logicNode as unknown as Record<string, unknown>).conceptRef as never;
      return (
        extractConceptNames(conceptRef)[0] ?? boldTermFromDescription ?? analyticalVariable.name
      );
    }
    return boldTermFromDescription ?? analyticalVariable.name;
  }, [logicNode, analyticalVariable.name, boldTermFromDescription]);

  // Standalone AV edit page provides contextDrillDownClick to navigate back to the CB page.
  // On the CB page itself the context prop is absent, so we fall back to the in-page drilldown.
  const onDrillDownClick = useCallback(() => {
    if (contextDrillDownClick) {
      contextDrillDownClick(analyticalVariable.id);
    } else {
      onViewEventClick(analyticalVariable.id);
    }
  }, [contextDrillDownClick, onViewEventClick, analyticalVariable.id]);

  const { isDrillDownDisabled, drillDownTooltip, isValidating } =
    useAVLogicErrors(analyticalVariable);

  return {
    intl,
    onDrillDownClick,
    logicNode,
    logicHeaderName,
    isDrillDownDisabled,
    drillDownTooltip,
    isValidating,
  };
};
